import React, { useState, useRef, useEffect } from 'react';
import { useAsync } from 'react-use-v13';
import screenfull from 'screenfull';
// eslint-disable-next-line restrict-imports/restrict-imports
import PlayArrow from '@material-ui/icons/PlayArrow';
import Video from './video';

interface VideoComparisonProps {
  srcVideoUrlA: string;
  srcVideoUrlB: string;
  onLoadOrError: (error: boolean) => void;
  caption?: string;
  onEnded?: () => void;
  allowFileDownload?: boolean;
  allowPlaybackRate?: boolean;
  forceFullscreen?: boolean;
  onlyVisibleOnLoadComplete?: boolean;
  preventSkip?: boolean;
  maximumExtraPlayingTime?: number;
  disableThumbnail?: boolean;
  disableVideoScaling?: boolean;
}

const VideoComparison: React.FC<VideoComparisonProps> = ({
  srcVideoUrlA,
  srcVideoUrlB,
  onLoadOrError,
  caption = '',
  onEnded,
  allowFileDownload = false,
  allowPlaybackRate = false,
  forceFullscreen = false,
  onlyVisibleOnLoadComplete = false,
  preventSkip = false,
  maximumExtraPlayingTime,
  disableVideoScaling,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentSource, setCurrentSource] = useState<null | string>(null);
  const [shouldPlay, setShouldPlay] = useState(false);

  const {
    loading,
    value: videoBlobUrls,
    error,
  } = useAsync(async () => {
    const resA = await fetch(srcVideoUrlA);
    const resB = await fetch(srcVideoUrlB);

    if (!resA.ok || !resB.ok) {
      await handleVideoEnd();
      throw new Error('Failed to load src content');
    }

    const blobA = await resA.blob();
    const blobB = await resB.blob();

    return {
      videoA: URL.createObjectURL(blobA),
      videoB: URL.createObjectURL(blobB),
    };
  }, [srcVideoUrlA, srcVideoUrlB]);

  useEffect(() => {
    if (currentSource !== null) {
      const timeout = setTimeout(() => setShouldPlay(true), 3000);
      return () => clearTimeout(timeout);
    }
  }, [currentSource]);

  const handleStart = () => {
    if (forceFullscreen) {
      screenfull.request(containerRef.current!);
    }
    setShouldPlay(false);
    setCurrentSource(videoBlobUrls?.videoA || null);
  };

  const handleVideoEnd = () => {
    if (currentSource === videoBlobUrls?.videoB) {
      onEnded && onEnded();
      screenfull.isEnabled && screenfull.isFullscreen && screenfull.exit();
    }
    setShouldPlay(false);
    setCurrentSource(
      videoBlobUrls && currentSource === videoBlobUrls.videoA ? videoBlobUrls.videoB : null,
    );
  };

  return (
    <div
      className="task-attachment__item task-attachment__item--videoComparison"
      ref={containerRef}
    >
      {loading || error || currentSource === null ? (
        <button
          onClick={handleStart}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            visibility: 'visible',
            background: '#808080',
            border: 'none',
            fontSize: '6rem',
          }}
        >
          <PlayArrow htmlColor="white" fontSize="inherit" />
        </button>
      ) : shouldPlay ? (
        <Video
          srcVideoUrl={currentSource}
          onLoadOrError={onLoadOrError}
          onEnded={handleVideoEnd}
          onPlay={() => {
            !screenfull.isFullscreen &&
              screenfull.isEnabled &&
              screenfull.request(containerRef.current!);
          }}
          maximumExtraPlayingTime={maximumExtraPlayingTime}
          allowFileDownload={allowFileDownload}
          caption={caption}
          allowPlaybackRate={allowPlaybackRate}
          onlyVisibleOnLoadComplete={onlyVisibleOnLoadComplete}
          preventSkip={preventSkip}
          disableThumbnail={false}
          forceFullscreen={false}
          skipSourceVideoLoadingCheck={true}
          onlyPlayWhenFullscreen={true}
          playing
          disableVideoScaling={disableVideoScaling}
        />
      ) : (
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            visibility: 'visible',
            background: '#808080',
            color: 'white',
            fontWeight: '700',
            fontSize: '6rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            userSelect: 'none',
          }}
        >
          Clip {currentSource === videoBlobUrls?.videoA ? 'A' : 'B'}
        </div>
      )}
    </div>
  );
};

export default VideoComparison;
